import { Loader2, AlertCircle } from 'lucide-react';
import { useLegalSettings } from '../hooks/useLegalSettings';

export function ImprintPage() {
  const { data: settings, isLoading, error } = useLegalSettings();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-white" />
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="glass-effect rounded-[15px] p-4">
        <div className="flex items-center gap-3 text-pp-orange">
          <AlertCircle className="w-5 h-5" />
          <p>Impressum wird derzeit eingerichtet.</p>
        </div>
      </div>
    );
  }

  const hasRequiredInfo = settings.owner_name && settings.street && settings.city && settings.email;

  if (!hasRequiredInfo) {
    return (
      <div className="glass-effect rounded-[15px] p-4">
        <div className="flex items-center gap-3 text-pp-orange">
          <AlertCircle className="w-5 h-5" />
          <p>Impressum wird derzeit eingerichtet.</p>
        </div>
      </div>
    );
  }

  const getOwnerLabel = () => {
    switch (settings.company_type) {
      case 'gmbh':
      case 'ug':
      case 'ag':
        return 'Geschäftsführer';
      case 'ev':
        return 'Vorstand';
      case 'gbr':
        return 'Gesellschafter';
      default:
        return null;
    }
  };

  const ownerLabel = getOwnerLabel();

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-white">Impressum</h1>
        <p className="text-white/60 mt-1">Angaben gemäß § 5 TMG</p>
      </div>

      <div className="glass-effect rounded-[15px] p-4 space-y-6">
        {/* Betreiber */}
        <section>
          <h2 className="font-semibold text-white mb-2">Betreiber</h2>
          <div className="text-white/80 space-y-1">
            {settings.company_name && <p className="font-medium text-white">{settings.company_name}</p>}
            {ownerLabel && settings.owner_name && (
              <p>{ownerLabel}: {settings.owner_name}</p>
            )}
            {!ownerLabel && settings.owner_name && <p>{settings.owner_name}</p>}
          </div>
        </section>

        {/* Adresse */}
        <section>
          <h2 className="font-semibold text-white mb-2">Anschrift</h2>
          <div className="text-white/80 space-y-1">
            <p>{settings.street}</p>
            <p>{settings.zip_code} {settings.city}</p>
            {settings.country && settings.country !== 'Deutschland' && (
              <p>{settings.country}</p>
            )}
          </div>
        </section>

        {/* Kontakt */}
        <section>
          <h2 className="font-semibold text-white mb-2">Kontakt</h2>
          <div className="text-white/80 space-y-1">
            {settings.email && (
              <p>
                E-Mail:{' '}
                <a href={`mailto:${settings.email}`} className="text-pp-orange hover:underline">
                  {settings.email}
                </a>
              </p>
            )}
            {settings.phone && <p>Telefon: {settings.phone}</p>}
          </div>
        </section>

        {/* Handelsregister */}
        {(settings.register_court || settings.register_number) && (
          <section>
            <h2 className="font-semibold text-white mb-2">Registereintrag</h2>
            <div className="text-white/80 space-y-1">
              {settings.register_court && <p>Registergericht: {settings.register_court}</p>}
              {settings.register_number && <p>Registernummer: {settings.register_number}</p>}
            </div>
          </section>
        )}

        {/* USt-IdNr */}
        {settings.vat_id && (
          <section>
            <h2 className="font-semibold text-white mb-2">Umsatzsteuer-ID</h2>
            <p className="text-white/80">
              Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: {settings.vat_id}
            </p>
          </section>
        )}

        {/* Verantwortlicher nach RStV */}
        {settings.responsible_person && (
          <section>
            <h2 className="font-semibold text-white mb-2">
              Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV
            </h2>
            <p className="text-white/80">{settings.responsible_person}</p>
            {settings.street && <p className="text-white/80">{settings.street}</p>}
            {(settings.zip_code || settings.city) && (
              <p className="text-white/80">{settings.zip_code} {settings.city}</p>
            )}
          </section>
        )}

        {/* Streitschlichtung */}
        <section>
          <h2 className="font-semibold text-white mb-2">Streitschlichtung</h2>
          <div className="text-white/80 space-y-2 text-sm">
            <p>
              Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS)
              bereit:{' '}
              <a
                href="https://ec.europa.eu/consumers/odr/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-pp-orange hover:underline"
              >
                https://ec.europa.eu/consumers/odr/
              </a>
            </p>
            <p>
              Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
              Verbraucherschlichtungsstelle teilzunehmen.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
